import React, { useState } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { analyseProposalStrategy } from "../api/prospectingReportsApi";

/**
 * GeeVee Strategy Advisor — the Proply "AI Strategy" tool, powered by GeeVee.
 * A slide-over that asks GeeVee to analyse the whole proposal's structure,
 * pricing, tone and persuasiveness and returns suggested improvements.
 *
 * The analysis is advisory only: it never edits the proposal.
 */
const GeeVeeStrategyPanel = ({ proposalId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState("");
  const [ran, setRan] = useState(false);

  const analyse = async () => {
    setLoading(true);
    try {
      const { data } = await analyseProposalStrategy(proposalId);
      setAnalysis(data.analysis || "");
      setRan(true);
      if (!data.available) toast("GeeVee is unavailable right now.");
    } catch (e) {
      toast.error("Could not run the analysis.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: "#0f8d9b" }} />
            <h2 className="text-lg font-bold text-gray-900">GeeVee Strategy</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!ran && !loading && (
            <>
              <p className="text-sm text-gray-600">
                GeeVee analyses your proposal's structure, pricing, tone and persuasiveness and
                suggests improvements to help win the listing.
              </p>
              <button onClick={analyse}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold"
                style={{ backgroundColor: "#0f8d9b" }}>
                <Sparkles className="w-4 h-4" /> Analyse with GeeVee
              </button>
              <p className="mt-3 text-xs text-gray-400">
                This only takes a moment. The analysis is advice — it never changes your proposal.
              </p>
            </>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mb-3" />
              <p className="text-sm">GeeVee is reviewing your proposal…</p>
            </div>
          )}

          {ran && !loading && (
            <>
              <div className="whitespace-pre-line text-sm text-gray-800 leading-relaxed">{analysis}</div>
              <button onClick={analyse}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700">
                <Sparkles className="w-4 h-4" /> Re-analyse
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeeVeeStrategyPanel;
